/**
 * DitherGpuBackend -- canonical backend interface for B0 onward.
 *
 * GpuCaps decision: capability.ts already exports `GpuCaps` with exactly
 * the shape prescribed in the architecture appendix §2 (maxTextureSize,
 * maxRenderbufferSize, vendor, renderer, precisionFormat, extensions).
 * Re-exporting it here keeps backend.ts as the single import site for
 * downstream code that needs both the interface and the caps type, while
 * avoiding a duplicate definition that could drift.
 *
 * No em-dashes in this file -- use hyphens or " -- " for inline asides.
 */

import type { GpuCaps } from "./capability";
import type { GpuRenderDiagnostics } from "./diagnostics";
import type { ReadbackStrategy } from "./readback-strategy";
import type { MethodParams } from "../types";

// Re-export GpuCaps from capability.ts -- it already matches appendix §2.
export type { GpuCaps } from "./capability";

export type { RenderBackend } from "./diagnostics";

export interface BackendSelectionOptions {
  preferred: "auto" | "webgl2" | "webgpu" | "metal-native";
  allowFallback: boolean;
}

export type GpuBackendTarget =
  | { kind: "webgl2"; canvas: HTMLCanvasElement | OffscreenCanvas }
  | { kind: "webgpu"; canvas?: HTMLCanvasElement | OffscreenCanvas }
  | { kind: "metal-native"; bridge: unknown };

export interface PlannedRegion {
  label: number;
  method: MethodParams;
  /** Populated when planFrameRoute rejected the region for GPU rendering. */
  reason?: string;
}

export interface GpuRegionBatchRequest {
  source: ImageData;
  labelMap?: ImageData;
  regions: PlannedRegion[];
  readbackStrategy: ReadbackStrategy;
}

export interface GpuRenderResult {
  output: ImageData;
  diagnostics: GpuRenderDiagnostics;
}

/**
 * Per-asset dimension caps used by the capability predicate to guard against
 * source or pattern images that exceed the device's texture size limit.
 * Semantically distinct from GpuCaps (device-level) -- this carries the
 * dimensions of the specific assets being rendered.
 */
export interface GpuAssetCaps {
  sourceWidth?: number;
  sourceHeight?: number;
  patternWidth?: number;
  patternHeight?: number;
}

export interface TexturePlan {
  format: "RGBA8" | "R8";
  width: number;
  height: number;
  filtering: "NEAREST" | "LINEAR";
  wrap: "CLAMP_TO_EDGE" | "REPEAT";
}

/**
 * Backend lifecycle is async-first. WebGL2 can initialise synchronously
 * internally, but still exposes `initialize(): Promise<void>` so WebGPU and
 * Metal-native backends (which require async adapter/device acquisition or
 * native FFI bridge setup) fit the same interface.
 *
 * `isReady()` returns true only AFTER `initialize()` resolves successfully.
 * UI must not show GPU available until `isReady() === true`.
 */
export interface DitherGpuBackend {
  readonly name: "webgl2" | "webgpu" | "metal-native";
  initialize(): Promise<void>;
  isReady(): boolean;
  getCaps(): GpuCaps;
  renderRegionBatch(req: GpuRegionBatchRequest): Promise<GpuRenderResult>;
  dispose(): void;
}

/**
 * Factory function for creating a GPU backend.
 *
 * B0.2 creates the real WebGL2DitherBackend. WebGPU and metal-native
 * remain explicit future-track errors until B6 and B7 land their proofs.
 */
export async function createDitherGpuBackend(
  target: GpuBackendTarget,
  _options: BackendSelectionOptions,
): Promise<DitherGpuBackend | null> {
  const { WebGL2DitherBackend } = await import("./webgl2-backend");

  switch (target.kind) {
    case "webgl2": {
      const backend = new WebGL2DitherBackend({ canvas: target.canvas });
      await backend.initialize();
      return backend;
    }
    case "webgpu":
      throw new Error(
        "createDitherGpuBackend: webgpu kind not yet supported (B6).",
      );
    case "metal-native":
      throw new Error(
        "createDitherGpuBackend: metal-native kind not yet supported (B7).",
      );
  }
}
